import { useEffect, useState } from 'react';
import { getMediaConfig, DISABLED_MEDIA_CONFIG, type MediaConfig } from '@/api/media';

/**
 * The upload policy, fetched once per page load and shared by every composer.
 *
 * Module-level cache rather than per-component state: the thread view mounts a composer per
 * open conversation, and the policy is a property of the tenant, not of the thread. Without
 * this, switching between five conversations would issue five identical requests.
 *
 * Starts at the fail-closed DISABLED default and only ever moves to whatever the server says,
 * so the paperclip cannot flash into existence before the policy is known.
 */
let cached: MediaConfig | null = null;
let inFlight: Promise<MediaConfig> | null = null;

function load(): Promise<MediaConfig> {
  if (cached) return Promise.resolve(cached);
  // getMediaConfig never rejects (it falls back to DISABLED), so a failed load simply leaves the
  // feature off. inFlight is cleared either way so a later mount can retry.
  inFlight ??= getMediaConfig().then((cfg) => {
    cached = cfg;
    inFlight = null;
    return cfg;
  });
  return inFlight;
}

export function useMediaConfig(): MediaConfig {
  const [config, setConfig] = useState<MediaConfig>(cached ?? DISABLED_MEDIA_CONFIG);

  useEffect(() => {
    let alive = true;
    load().then((cfg) => {
      if (alive) setConfig(cfg);
    });
    return () => {
      alive = false;
    };
  }, []);

  return config;
}

/** Test seam — resets the module cache between cases. */
export function __resetMediaConfigCache() {
  cached = null;
  inFlight = null;
}
